import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@web/base/shared/components/base/base.component';
import { OpenaiService } from '@web/base/shared/openai.service';

import { io, Socket } from 'socket.io-client';
import { URL, URL_SOCKET } from './../../../../environments/environment';
import { ConstantsProject } from '@web/project/shared/constants.class';
import { cleanMarkdown, getCookie, markdownToHTML, setCookie } from '@web/base/shared/utils';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@web/base/auth/auth.service';
import { Router } from '@angular/router';

@Component({
  selector: 'web-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent extends BaseComponent implements OnInit, AfterViewInit {

  @ViewChild('inputMessage') inputMessage: ElementRef;

  public form: UntypedFormGroup;
  open = false;
  userMessage = '';
  chatHistory: { role: string, content: string, references?: any[] }[] = [];
  waiting = false;

  chatHistoryLayer;

  zoom = null;
  zoomed = false;

  private socket: Socket;

  constructor(
    translate: TranslateService,
    dialog: MatDialog,
    private openai: OpenaiService,
    private authService?: AuthService,
    private router?: Router
  ) {
    super(translate, null, dialog);
  }

  ngOnInit() {
    this.getTranslations(['core', 'chat']);
    this.createForm();
  }

  createForm() {
    this.form = new UntypedFormGroup({
      threadId: new UntypedFormControl(null),
      text: new UntypedFormControl('', [Validators.required])
    });

    // Inicializamos chatHistory con el texto inicial
    this.chatHistory.push({ role: 'assistant', content: this.translations['chat']['textInitial'] });
  }

  ngAfterViewInit() {
    this.chatHistoryLayer = document.querySelector('.chat-history');

    setTimeout(() => {
      this.chatHistoryLayer.scrollTop = this.chatHistoryLayer.scrollHeight;
    }, 0);

    // Si no existe la cookie de primera vez abrimos el char
    if (!getCookie(ConstantsProject.COOKIES_CHAT_AI)) {
      this.openChat();
    }
  }

  send() {
    if (!this.waiting && this.form.valid) {
      this.waiting = true;

      // Mostrar el mensaje del usuario
      this.chatHistory.push({ role: 'user', content: this.form.controls['text'].value });

      // Scrolleamos hasta el final del chat
      setTimeout(() => {
        this.chatHistoryLayer.scrollTop = this.chatHistoryLayer.scrollHeight;
      }, 0);

      if (!this.socket) {
        // this.socket = io(URL.replace('/web', ''));
        this.socket = io(URL_SOCKET);

        this.socket.on('connect', () => {
          console.log('Connected to socket.io server');
        });

        this.socket.on('disconnect', () => {
          console.log('Disconnected from socket.io server');
          this.socket.close();
        });

        this.socket.on('error', (err) => {
          console.log('Error connecting to socket.io server', err);
        });

        this.socket.on('error-assistant', (data) => {
          this.chatHistory.push({
            role: 'assistant',
            content: this.translations['chat']['errorResponse'],
            references: null
          });

          this.waiting = false;
        });

        this.socket.on('answer-assistant', (data) => {
          // Si la respuesta del asistente no está asociada a un contenido concreto, intentamos determinar la URL del tipo de contenido que se está buscando (cursos, productos, ...).
          if (!data.hasAnswer) {
            this.getURLByAnswerType(data.answerType, data.references);
          }

          // La respuesta nos va llegando poco a poco por partes. Si es la primera parte, hacemos push de la respuesta al array del chat.
          // Si no es la primera parte, sobreescribimos el contenido de la respuesta en el índice correspondiente.

          // Se guardan los índices que no tiene URL
          const indicesSinUrl = data.references
            .map((ref, index) => ref.url == null ? index : null)
            .filter(index => index !== null);

          // Se filtran los índices que no tiene URL
          const filteredReferences = data.references.filter(ref => ref.url != null);

          if (this.chatHistory.length > data.answerIndex) {
            this.chatHistory[data.answerIndex].content = cleanMarkdown(data.text);
            this.chatHistory[data.answerIndex].references = filteredReferences;
          } else {

            // devolvemos toda la contestación
            this.chatHistory.push({
              role: 'assistant',
              content: cleanMarkdown(data.text),
              references: filteredReferences.length ? filteredReferences : null
            });

          }

          this.form.controls['threadId'].setValue(data.threadId);

          // Vamos haciendo scroll conforme vamos recibiendo respuestas del asistente.
          setTimeout(() => {
            this.chatHistoryLayer.scrollTop = this.chatHistoryLayer.scrollHeight;
          }, 0);

          // Si recibimos la respuesta completa del asistente, parseamos el contenido de Markdown a HTML.
          if (data.completed) {
            this.waiting = false;

            let cleanedText = this.chatHistory[this.chatHistory.length - 1].content;
            // Limpiamos los [indice] que no tiene URL
            indicesSinUrl.forEach(index => {
              const tagRegex = new RegExp(`\\[${index}\\]`, 'g');
              cleanedText = cleanedText.replace(tagRegex, '');
            });

            let html = markdownToHTML(cleanedText);
            // Si el usuario no esta logueado ponemos la coletilla de suscriptor
            if (!this.authService.user) {
              html += `Si aún no eres suscriptor/a, <a href="/informacion/suscripciones" class="router-link">suscríbete aquí</a>`;
            }
            // Parseamos la respuesta del asistente a HTML
            this.chatHistory[this.chatHistory.length - 1].content = html;
          }
        });
      }

      this.socket.emit('ask-assistant', {
        answerIndex: this.chatHistory.length,
        token: localStorage.getItem(ConstantsProject.TOKEN_KEY),
        text: this.form.controls['text'].value,
        threadId: this.form.controls['threadId'].value
      });

      this.form.controls['text'].setValue('');
    }
  }

  openChat() {
    this.open = true;
    document.getElementById('__chat_esperanto').classList.remove('first-time');
    // this.inputMessage.nativeElement.focus(); // Quitamos el autofocus porque en el iPhone es muy intrusivo.
  }

  closeChat() {
    this.open = false;
    this.zoom = null;
    this.zoomed = false;

    // Si no existe la cookie de primera vez la creamos
    if (getCookie(ConstantsProject.COOKIES_CHAT_AI) === null) {
      setCookie(ConstantsProject.COOKIES_CHAT_AI, 'true', 365);
    }
  }

  clearChat() {
    this.chatHistory = [];
    this.chatHistory.push({ role: 'assistant', content: this.translations['chat']['textInitial'] });
  }

  toggleZoom() {
    this.zoomed = !this.zoomed;
    this.zoom = this.zoomed ? 'zoomIn' : 'zoomOut';
  }

  copyMessage(message: any) {
    navigator.clipboard.writeText(message);
  }

  onEnter(event: KeyboardEvent): void {
    event.preventDefault(); // Previene el salto de línea
    if (this.form.valid) {
      this.send();
    }
  }

  getURLByAnswerType(answerType: any, references: any[]) {

    // añadir a referencias otro item más
    switch (answerType.toLowerCase()) {
      case 'cursos':
        references.push({
          title: this.translations['chat']['curseList'],
          url: '/cursos/1/online'
        });
        break;
      case 'productos':
        references.push({
          title: this.translations['chat']['shop'],
          url: '/productos'
        });
        break;
      case 'consultas':
        references.push({
          title: this.translations['chat']['contacts'],
          url: '/consultas/presentacion'
        });

        break;
    }
  }

  openLegalAI() {
    this.openSimpleDialog(this.translations['core']['legalAI'], this.translations['core']['legalAIContent']);
  }

  ngAfterViewChecked() {
    const links = document.querySelectorAll('.router-link');

    links.forEach(link => {
      link.addEventListener('click', (event: Event) => {
        event.preventDefault();
        const target = (event.target as HTMLAnchorElement).getAttribute('href');
        if (target) {
          this.router.navigateByUrl(target);
        }
      });
    });
  }
}
